import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar, Clock, Ticket, Users, MessageSquare } from 'lucide-react';
import { motion } from 'framer-motion';
import { eventsAPI, socialAPI, guidanceAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import ShareButton from '../components/shared/ShareButton';
import WatchButton from '../components/guidance/WatchButton';
import NextActionCards from '../components/guidance/NextActionCards';
import { useSmartNotify } from '../components/guidance/SmartNotificationProvider';
import { toast } from 'sonner';

export default function EventDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const { triggerSmartNotify } = useSmartNotify();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTier, setSelectedTier] = useState('');
  const [qty, setQty] = useState(1);
  const [purchasing, setPurchasing] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');

  useEffect(() => {
    const fetch = async () => {
      try {
        const [evRes, commRes] = await Promise.all([
          eventsAPI.get(id),
          socialAPI.getComments('event', id),
        ]);
        setEvent(evRes.data);
        setComments(commRes.data);
        if (evRes.data?.title) {
          guidanceAPI.trackActivity('view', 'event', id, evRes.data.title).catch(() => {});
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, [id]);

  const handlePurchase = async () => {
    if (!selectedTier) { toast.error('Select a ticket tier'); return; }
    setPurchasing(true);
    try {
      const res = await eventsAPI.purchase(id, { tier_name: selectedTier, quantity: qty });
      toast.success(res.data.message);
      triggerSmartNotify('purchased_ticket', 'event', id);
      const updated = await eventsAPI.get(id);
      setEvent(updated.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Purchase failed'); }
    finally { setPurchasing(false); }
  };

  const handleComment = async () => {
    if (!newComment.trim()) return;
    try {
      const res = await socialAPI.addComment({ content_type: 'event', content_id: id, text: newComment });
      setComments([res.data, ...comments]);
      setNewComment('');
    } catch (e) { toast.error('Failed'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-zinc-50"><div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!event) return <div className="min-h-screen bg-zinc-50 flex items-center justify-center"><p className="text-zinc-500">Event not found</p></div>;

  const totalCap = event.ticket_tiers?.reduce((a, b) => a + b.quantity, 0) || 0;

  return (
    <div className="min-h-screen bg-zinc-50 pb-12" data-testid="event-detail-page">
      <div className="bg-gradient-to-br from-violet-600 to-pink-500 h-48 relative">
        <div className="max-w-5xl mx-auto px-4 pt-4 flex justify-between">
          <Link to="/events" className="text-white/70 hover:text-white"><ArrowLeft size={24} /></Link>
          <div className="flex items-center gap-3">
            <WatchButton entityType="event" entityId={id} size="md" />
            <ShareButton contentType="product" contentId={id} title={event.title} />
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 -mt-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white rounded-2xl border border-zinc-200 p-6">
              <div className="flex items-start gap-4">
                <div className="bg-violet-100 rounded-xl px-4 py-3 text-center shrink-0">
                  <p className="font-bold text-2xl text-violet-700 leading-none">{new Date(event.event_date).getDate()}</p>
                  <p className="text-xs text-violet-500">{new Date(event.event_date).toLocaleDateString('en-US', { month: 'short' })}</p>
                </div>
                <div>
                  <h1 className="font-bold text-2xl text-zinc-900" data-testid="event-title">{event.title}</h1>
                  <p className="text-sm text-zinc-500">by {event.creator_name}</p>
                  <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-zinc-500">
                    <span className="flex items-center gap-1"><MapPin size={12} /> {event.location}</span>
                    {event.venue && <span className="flex items-center gap-1">{event.venue}</span>}
                    <span className="flex items-center gap-1"><Clock size={12} /> {event.event_time}</span>
                  </div>
                </div>
              </div>
              <p className="text-zinc-700 mt-4 leading-relaxed whitespace-pre-wrap">{event.description}</p>
            </div>

            {/* Comments */}
            <div className="bg-white rounded-2xl border border-zinc-200 p-6">
              <h2 className="font-bold text-lg text-zinc-900 mb-4 flex items-center gap-2"><MessageSquare size={18} /> Discussion ({comments.length})</h2>
              {user && (
                <div className="flex gap-2 mb-4">
                  <Input placeholder="Join the conversation..." value={newComment} onChange={e => setNewComment(e.target.value)} className="flex-1" data-testid="event-comment-input" />
                  <Button onClick={handleComment} className="bg-violet-600 text-white">Post</Button>
                </div>
              )}
              <div className="space-y-3">
                {comments.map(c => (
                  <div key={c.id} className="p-3 bg-zinc-50 rounded-xl">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-semibold text-zinc-700">{c.user_name}</span>
                      <span className="text-zinc-400">{new Date(c.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className="text-sm text-zinc-600">{c.text}</p>
                  </div>
                ))}
                {comments.length === 0 && <p className="text-xs text-zinc-400 text-center py-4">Be the first to comment</p>}
              </div>
            </div>
          </div>

          {/* Sidebar - Tickets */}
          <div>
            <div className="bg-white rounded-2xl border border-zinc-200 p-6 sticky top-4">
              <h2 className="font-bold text-lg text-zinc-900 mb-1">Attend This Event</h2>
              <p className="text-xs text-zinc-500 mb-4">{event.total_tickets_sold}/{totalCap} sold</p>

              <div className="space-y-2 mb-4">
                {event.ticket_tiers?.map((tier, i) => {
                  const avail = tier.quantity - tier.sold;
                  return (
                    <button key={i} onClick={() => avail > 0 && setSelectedTier(tier.name)}
                      className={`w-full text-left p-3 rounded-xl border-2 transition-all ${avail === 0 ? 'opacity-50 cursor-not-allowed border-zinc-200' : selectedTier === tier.name ? 'border-violet-500 bg-violet-50' : 'border-zinc-200 hover:border-zinc-300'}`}
                      disabled={avail === 0}
                      data-testid={`ticket-tier-${tier.name}`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-zinc-900 text-sm">{tier.name}</span>
                        <span className="font-bold text-violet-600">{tier.price === 0 ? 'Free' : formatCurrency(tier.price)}</span>
                      </div>
                      <p className="text-[10px] text-zinc-500 mt-0.5">{tier.perks}</p>
                      <p className="text-[10px] text-zinc-400 mt-1">{avail > 0 ? `${avail} available` : 'Sold out'}</p>
                    </button>
                  );
                })}
              </div>

              {selectedTier && (
                <div className="space-y-3 pt-3 border-t border-zinc-100">
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-zinc-500">Qty:</label>
                    <Input type="number" min={1} max={10} value={qty} onChange={e => setQty(Math.max(1, parseInt(e.target.value) || 1))} className="w-20 text-center" data-testid="ticket-qty" />
                  </div>
                  <Button onClick={handlePurchase} disabled={purchasing} className="w-full bg-violet-600 text-white font-bold h-11" data-testid="purchase-ticket-btn">
                    {purchasing ? 'Processing...' : `Get ${qty} Ticket${qty > 1 ? 's' : ''}`}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Cross-Mode Next Action Cards */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        <NextActionCards contextPage="event_detail" />
      </div>
    </div>
  );
}
